import { Component, ViewChild } from '@angular/core';
import {
  IonContent,
  ModalController,
  PopoverController,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { ViewDidEnter } from '@ionic/angular';
import { PanAppState, PlatformEnum, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { filter, Observable, Subscription, take, tap } from 'rxjs';
import { CustomerSelectionTouchComponent } from '@shared/components/header/customer-selection/customer-selection-touch/customer-selection-touch.component';
import { ModalService } from '@shared/services/modal/modal.service';
import { SimpleActionModalComponent } from '@shared/components/simple-action-modal/simple-action-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { Customer } from '@usf/customer-types/customer';
import { Department } from '@usf/customer-types';
import { HomeService } from '@app/home/services/home.service';
import { NavigationSkipped, Router } from '@angular/router';
import { CustomerSelectionDesktopComponent } from '@shared/components/header/customer-selection/customer-selection-desktop/customer-selection-desktop.component';
import { ImportOrderTransaction } from '@usf/ngrx-order/lib/reducers/import-orders/import-orders.reducer';
import { OrderItem } from '@usf/order-types';
import {
  CRITICAL_ERRORS,
  INVALID_ORDER_ITEM,
  ImportOrderValidationErrorTypeEnum,
  MINOR_ERRORS,
} from './constants/import-order-validation.constants';
import { DownloadImportOrderModalComponent } from '@shared/components/import-order/download-import-order-modal/download-import-order-modal.component';
import { ImportOrderValidationService } from '../../services/import-order-validation.service';
import { ImportOrderDownloadService } from '@shared/services/file-download/import-order-download.service';
import { OrderTracingService } from '@order/tracking/tracing/order-tracing.service';
import { OrderService } from '@order/services/order.service';
import { CalendarComponent } from '@shared/components/calendar/calendar.component';
import moment from 'moment';
import { QuantityUpdate } from '@shared/models/quantity-update';

@Component({
  selector: 'app-import-order-validation',
  templateUrl: './import-order-validation.page.html',
  styleUrls: ['./import-order-validation.page.scss'],
})
export class ImportOrderValidationPage
  implements ViewDidEnter, ViewWillEnter, ViewWillLeave
{
  @ViewChild(IonContent, { static: false }) content: IonContent;
  public enumTypes = ImportOrderValidationErrorTypeEnum;
  public criticalErrors = CRITICAL_ERRORS;
  public breadcrumbs: Crumb[];
  public isInvalidOrderSectionOpen = true;
  public isValidOrderSectionOpen = true;
  public importErrors$: Observable<string[]> =
    this.importOrderService.fileImportErrors$();
  public invalidOrders: ImportOrderTransaction[] = [];
  public validOrders: ImportOrderTransaction[] = [];
  public flagsForProducts = {};
  public customers: Customer[] = [];
  public eligibleDeliveryDays: number[];
  public initialDeliveryDate: string;
  private customersDeliveryDays = new Map<number, number[]>();
  private customersConfirmDeliveryDates = new Map<number, string>();
  private routerNavigationSkipSub: Subscription;
  private today = moment().utc();
  public substitutesForValidOrders = new Map<number, number[]>();
  public substitutesForInvalidOrders = new Map<number, number[]>();
  confirmModalOpen = false;
  loading$ = this.importOrderService.loading$();
  loadingSub: Subscription;

  dateClass = (cellDate: moment.Moment, view: string) => {
    /* Highlight available dates inside the month view */
    return view === 'month' &&
      cellDate.isSameOrAfter(moment()) &&
      cellDate.isSameOrAfter(moment.utc(this.initialDeliveryDate)) &&
      this.eligibleDeliveryDays?.includes(cellDate.day())
      ? 'available-delivery-date-color'
      : '';
  };

  dateFilter = (date: moment.Moment): boolean => {
    return (
      date.isSameOrAfter(moment()) &&
      date.isSameOrAfter(moment.utc(this.initialDeliveryDate)) &&
      this.eligibleDeliveryDays?.includes(date.day())
    );
  };

  constructor(
    private importOrderService: ImportOrderValidationService,
    private importOrderDownloadService: ImportOrderDownloadService,
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    readonly router: Router,
    private translateService: TranslateService,
    private popoverController: PopoverController,
    private modalController: ModalController,
    private modalService: ModalService,
    private customerStoreService: CustomerStoreService,
    private homeService: HomeService,
    private orderTracingService: OrderTracingService,
    readonly orderService: OrderService,
    private translate: TranslateService,
  ) {}

  ionViewDidEnter(): void {
    this.getData();
    this.loadingSub = this.loading$
      .pipe(
        tap(loading => {
          if (!loading) {
            this.importOrderService.tracePageLoad(
              [...this.validOrders, ...this.invalidOrders].length,
            );
          }
        }),
      )
      .subscribe();
  }

  ionViewWillLeave(): void {
    if (!!this.routerNavigationSkipSub)
      this.routerNavigationSkipSub.unsubscribe();
    if (!!this.loadingSub) this.loadingSub.unsubscribe();
  }

  getData(): void {
    this.goToTop();
    this.importOrderService
      .orders$()
      .pipe(take(1))
      .subscribe((ot: ImportOrderTransaction[]) => {
        const filteredOt = ot.reduce(
          (res, orderTransaction) => {
            if (!!orderTransaction) {
              let order = { ...orderTransaction };
              this.getDepartmentsForCustomerNumber(order);
              this.flagsForProducts[order.clientId] = false;
              if (!!order.orderItems) {
                const counter = this.orderItemsCounter(order);
                const hasError = counter.invalid > 0;
                const orderItems = [];
                order.orderItems.forEach((oi: OrderItem) => {
                  orderItems.push({
                    ...oi,
                    originalUnits: oi.unitsOrdered,
                    originalEaches: oi.eachesOrdered,
                  });
                });
                order.orderItems = [];
                order.orderItems = orderItems;
                res[hasError ? 'invalid' : 'valid'].push(order);

                if (this.isValidOrderWithInvalidProduct(order)) {
                  this.flagsForProducts[order.clientId] = true;
                }
              }
            }
            return res;
          },
          { invalid: [], valid: [] },
        );

        this.invalidOrders = filteredOt['invalid'];
        this.validOrders = filteredOt['valid'];
        this.substitutesForValidOrders = new Map<number, number[]>();
        this.substitutesForInvalidOrders = new Map<number, number[]>();
      });
  }

  ionViewWillEnter(): void {
    this.routerNavigationSkipSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationSkipped) {
        this.getData();
      }
    });
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myOrders',
        url: '/desktop/my-orders-view',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.validate',
        url: '',
        active: true,
      },
    ];
    this.importOrderService.trackPageLoadAnalytics();
  }

  orderItemsCounter(ot: ImportOrderTransaction): {
    invalid;
    valid;
    substitutes;
  } {
    if (!ot?.orderItems) return { invalid: 0, valid: 0, substitutes: 0 };
    const substitutes = ot.orderItems?.filter(
      oi => !!oi?.subOrReplacementProduct,
    )?.length;
    if (CRITICAL_ERRORS.includes(ot.orderHeaderErrorCode)) {
      return {
        invalid: ot.orderItems?.filter(oi => !!oi)?.length,
        valid: 0,
        substitutes,
      };
    } else {
      const invalidCounter =
        ot.orderItems.filter(
          oi => !!oi && CRITICAL_ERRORS.includes(oi.orderItemErrorCode),
        )?.length || 0;
      return {
        invalid: invalidCounter,
        valid: ot.orderItems?.filter(oi => !!oi)?.length - invalidCounter,
        substitutes,
      };
    }
  }

  toggleOrderSection(section: number) {
    if (section === 1) {
      this.isInvalidOrderSectionOpen = !this.isInvalidOrderSectionOpen;
    } else {
      this.isValidOrderSectionOpen = !this.isValidOrderSectionOpen;
    }
  }

  async confirmCancel() {
    if (this.platformService.platformType === PlatformEnum.desktop) {
      this.modalService.setModalOptions(
        false,
        SimpleActionModalComponent,

        {
          withIcon: true,
          title: this.translateService?.instant(
            'i18n.importOrderPage.confirmCancelTitle',
          ),
          content: this.translateService?.instant(
            'i18n.importOrderPage.confirmCancelDesc',
          ),
          leftButtonClass: 'usf-outline-green-button',
          leftButtonText: this.translateService?.instant('i18n.common.cancel'),
          rightButtonClass: 'usf-fill-orange-button',
          rightButtonText: this.translateService?.instant(
            'i18n.importOrderPage.continueImport',
          ),
          exitButtonForTablet: false,
          exitButtonForMobile: false,
        },
        'simple-action-modal',
      );
    } else {
      this.modalService.setModalOptions(
        true,
        SimpleActionModalComponent,

        {
          withIcon: true,
          title: this.translateService?.instant(
            'i18n.importOrderPage.confirmCancelTitle',
          ),
          content: this.translateService?.instant(
            'i18n.importOrderPage.confirmCancelDesc',
          ),
          leftButtonClass: 'usf-outline-green-button',
          leftButtonText: this.translateService?.instant(
            'i18n.importOrderPage.cancelImport',
          ),
          rightButtonClass: 'usf-fill-orange-button',
          rightButtonText: this.translateService?.instant(
            'i18n.importOrderPage.continueImport',
          ),
          exitButtonForTablet: false,
          exitButtonForMobile: false,
        },
        'simple-action-modal',
      );
    }
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then((resp: any) => {
      if (!!resp?.data?.dispatchAction) {
        this.importOrderService.cancel();
      }
    });

    return await modal.present();
  }

  async confirmRemove(otIdx: number, oiIdx: number, isValidOrder?: boolean) {
    const isOrder = oiIdx === -1;
    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      SimpleActionModalComponent,
      {
        withIcon: false,
        title: this.translateService?.instant(
          isOrder
            ? 'i18n.importOrderPage.confirmRemoveOrderDesc'
            : 'i18n.importOrderPage.confirmRemoveProductDesc',
        ),
        exitButtonForTablet: false,
        exitButtonForMobile: false,
      },
      'simple-action-modal',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss()?.then((resp: any) => {
      if (resp?.data?.dispatchAction) {
        if (isOrder) {
          this.removeOrder(otIdx, isValidOrder);
        } else {
          this.removeOrderItem(otIdx, oiIdx, isValidOrder);
        }
      }
    });

    return await modal.present();
  }

  removeOrder(otIdx: number, isValidOrder?: boolean): ImportOrderTransaction {
    let otCopy;
    if (isValidOrder) {
      otCopy = this.validOrders.splice(otIdx, 1, null)[0];
      this.substitutesForValidOrders.delete(otIdx);
    } else {
      otCopy = this.invalidOrders.splice(otIdx, 1, null)[0];
      this.substitutesForInvalidOrders.delete(otIdx);
    }
    return otCopy;
  }

  removeOrderItem(otIdx: number, oiIdx: number, isValidOrder?: boolean): void {
    let subIdx;
    if (isValidOrder) {
      const oiCopy = [...this.validOrders[otIdx]?.orderItems];
      if (!oiCopy) return;
      oiCopy?.splice(oiIdx, 1);
      this.validOrders[otIdx].orderItems = oiCopy;
      subIdx = this.substitutesForValidOrders.get(otIdx)?.indexOf(oiIdx);
    } else {
      const oiCopy = [...this.invalidOrders[otIdx]?.orderItems];
      if (!oiCopy) return;
      oiCopy?.splice(oiIdx, 1);
      this.invalidOrders[otIdx].orderItems = oiCopy;
      const otCopy = { ...this.invalidOrders[otIdx] };
      const counter = this.orderItemsCounter(otCopy);
      if (counter.invalid === 0) {
        //NO INVALID ITEMS -> SET ORDER AS VALID
        this.removeOrder(otIdx);
        const idx = this.validOrders.push(otCopy);
        const sbs = this.substitutesForInvalidOrders.get(otIdx);
        if (!!sbs) {
          this.substitutesForValidOrders.set(idx - 1, sbs);
          this.substitutesForInvalidOrders.delete(otIdx);
        }
        this.changeShowFlagStatusFor(otCopy.clientId);
      }
      subIdx = this.substitutesForInvalidOrders.get(otIdx)?.indexOf(oiIdx);
    }
    if (isValidOrder && subIdx > -1) {
      const sbs = this.substitutesForValidOrders.get(otIdx);
      sbs?.splice(subIdx, 1);
      this.substitutesForValidOrders.set(otIdx, sbs || []);
    } else if (!isValidOrder && subIdx > -1) {
      const sbs = this.substitutesForInvalidOrders.get(otIdx);
      sbs?.splice(subIdx, 1);
      this.substitutesForInvalidOrders.set(otIdx, sbs || []);
    }
  }

  changeShowFlagStatusFor(clientId: string): void {
    this.flagsForProducts[clientId] = !this.flagsForProducts[clientId];
  }

  private switchCustomerForOrder(
    otIdx: number,
    isValidOrder: boolean,
    data: { customerNumber; departmentNumber },
  ): void {
    let order = isValidOrder
      ? this.validOrders[otIdx]
      : this.invalidOrders[otIdx];
    if (isValidOrder) {
      if (order?.customerNumber === data?.customerNumber) {
        order.departmentNumber = data?.departmentNumber;
      }
    } else {
      if (order?.customerNumber === data?.customerNumber) {
        order.departmentNumber = data?.departmentNumber;
        if (
          order.orderHeaderErrorCode === this.enumTypes.CRITICAL_ERROR_104 ||
          order.orderHeaderErrorCode === this.enumTypes.CRITICAL_ERROR_105
        ) {
          //REMOVE HEADER ERROR
          order.orderHeaderErrorCode = '';
          order.orderHeaderErrorMsg = '';
          const counter = this.orderItemsCounter(order);
          if (counter.invalid === 0) {
            //ORDER SHOULD BE VALID
            this.removeOrder(otIdx);
            this.validOrders.push(order);
          }
        }
      }
    }
  }

  async openDesktopCustomerSelectionModal(
    otIdx: number,
    isValidOrder: boolean,
    ev: any,
  ) {
    const order = isValidOrder
      ? this.validOrders[otIdx]
      : this.invalidOrders[otIdx];
    const searchText$ = new Observable<string>(observer => {
      observer.next(order.customerNumber.toString());
      observer.complete();
    });
    const popover = await this.popoverController.create({
      component: CustomerSelectionDesktopComponent,
      componentProps: {
        searchText$: searchText$,
        vm$: this.getCustomerSelectionVM$(searchText$),
        showSearchbar: false,
        selectedDepartmentNumber: order.departmentNumber?.toString(),
      },
      cssClass: 'customer-selection-popover-desktop',
      event: ev,
      mode: 'md',
      showBackdrop: true,
      backdropDismiss: true,
      translucent: true,
    });
    popover.onDidDismiss().then((resp: any) => {
      if (!!resp?.data?.departmentNumber) {
        this.switchCustomerForOrder(otIdx, isValidOrder, resp.data);
      }
    });
    await popover.present();
  }

  async openTouchCustomerSelectionModal(otIdx: number, isValidOrder: boolean) {
    const order = isValidOrder
      ? this.validOrders[otIdx]
      : this.invalidOrders[otIdx];
    const searchText$ = new Observable<string>(observer => {
      observer.next(order.customerNumber.toString());
      observer.complete();
    });
    this.modalService.setModalOptions(true, CustomerSelectionTouchComponent, {
      searchText$: searchText$,
      vm$: this.getCustomerSelectionVM$(searchText$),
      showSearchbar: false,
      selectedDepartmentNumber: order.departmentNumber?.toString(),
    });
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then((resp: any) => {
      if (!!resp?.data?.departmentNumber) {
        this.switchCustomerForOrder(otIdx, isValidOrder, resp.data);
      }
    });
    await modal.present();
  }

  async openSelectionModal(otIdx: number, isValidOrder: boolean, event: any) {
    if (this.platformService.platformType === PlatformEnum.desktop) {
      await this.openDesktopCustomerSelectionModal(otIdx, isValidOrder, event);
    } else {
      await this.openTouchCustomerSelectionModal(otIdx, isValidOrder);
    }
  }

  async getDepartmentsForCustomerNumber(order: ImportOrderTransaction) {
    const customerNumber = order?.customerNumber;
    const cus = this.customers.find(
      cus => cus.customerNumber === customerNumber,
    );
    if (!!cus) return;

    const dif = moment
      .utc(order?.confirmedDeliveryDate)
      .diff(this.today, 'days');
    if (dif > 7) {
      this.customersConfirmDeliveryDates[customerNumber] = this.today.date();
    } else {
      this.customersConfirmDeliveryDates[customerNumber] =
        order.confirmedDeliveryDate;
    }

    this.orderService
      .customerDeliveryDaysWithNumber(customerNumber)
      .pipe(
        filter(sd => !!sd),
        take(1),
      )
      .subscribe(
        selectedDays =>
          (this.customersDeliveryDays[customerNumber] = selectedDays),
      );

    this.customerStoreService
      ?.loadCustomerWithNumber$(customerNumber)
      .pipe(take(1))
      .subscribe(customer => {
        this.customers.push(customer);
      });
  }

  getCustomerInfo(customerNumber: number, otIdx = -1): Customer {
    let cust: Customer = this.customers?.find(
      cus => cus.customerNumber === customerNumber,
    );
    if (!cust) {
      cust = {
        customerNumber,
        customerName: '',
        departments: [],
      } as Customer;
      if (otIdx > -1) {
        this.invalidOrders[otIdx].orderHeaderErrorCode =
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_101;
        this.invalidOrders[otIdx].orderHeaderErrorMsg =
          'The customer number was not recognized';
      }
    }
    return cust;
  }

  private getCustomerDepartmentInfo(
    customerNumber: number,
    departmentNumber: string,
  ): Department {
    const cust = this.getCustomerInfo(customerNumber);
    if (!!cust?.departments) {
      return cust.departments.find(
        dep => dep.departmentNumber == departmentNumber,
      );
    }
  }

  getCustomerDepartmentName(
    customerNumber: number,
    dpNumber: string | number,
  ): string {
    let departmentNumber = '';
    if (!!dpNumber) {
      departmentNumber = dpNumber.toString();
    }
    const dpInfo = this.getCustomerDepartmentInfo(
      customerNumber,
      departmentNumber,
    );
    if (!!dpInfo) {
      return `${dpInfo.departmentName} (${dpInfo.departmentNumber})`;
    }
    return this.translateService?.instant(
      'i18n.importOrderPage.selectDepartment',
    );
  }

  getCustomerSelectionVM$(searchText: Observable<string>) {
    return this.homeService.getCustomerSelectionVM$(searchText);
  }

  async confirmSubmitImport() {
    this.loading$.pipe(take(1)).subscribe(async loading => {
      if (loading) {
        // If loading is true, don't proceed with submission
        return;
      }
      if (this.invalidProductsCounter > 0) {
        this.modalService.setModalOptions(
          this.platformService.isTouch.value,
          SimpleActionModalComponent,
          {
            withIcon: true,
            title: this.translateService?.instant(
              'i18n.importOrderPage.confirmSubmitTitle',
            ),
            content: this.translateService?.instant(
              'i18n.importOrderPage.confirmSubmitContent',
            ),
            leftButtonText: this.translateService?.instant(
              'i18n.importOrderPage.cancel',
            ),
            rightButtonText: this.translateService?.instant(
              'i18n.importOrderPage.continue',
            ),
            exitButtonForTablet: false,
            exitButtonForMobile: false,
          },
          'simple-action-modal',
        );
        const modal = await this.modalController.create(
          this.modalService.modalOptions,
        );

        modal.onDidDismiss()?.then((resp: any) => {
          if (resp?.data?.dispatchAction) {
            this.submitImport();
          }
        });

        await modal.present();
      } else {
        this.submitImport();
      }
    });
  }

  submitImport() {
    const ordersToSubmit: ImportOrderTransaction[] = [];
    const allowedOrdersToSubmit: ImportOrderTransaction[] = JSON.parse(
      JSON.stringify(
        this.invalidOrders
          .reduce((res, order) => {
            if (this.isValidOrderWithInvalidProduct(order)) {
              res.push(order);
            }
            return res;
          }, [])
          .concat(this.validOrders),
      ),
    );

    allowedOrdersToSubmit.forEach((order: ImportOrderTransaction) => {
      if (!order) return;
      order.decomposeFlag = true;
      order.importOrderDtm = new Date().toISOString();
      if (
        order.orderHeaderErrorCode !==
        ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_105
      ) {
        const orderItems: OrderItem[] = [];
        const REMOVE_ERRORS_ON_SUBMIT: string[] = [
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_105,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_106,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1003,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1004,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1006,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1005,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1008,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1009,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1011,
          ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_1012,
        ];
        order.orderItems.forEach((orderItem: OrderItem) => {
          if (!orderItem) return;
          if (!REMOVE_ERRORS_ON_SUBMIT.includes(orderItem.orderItemErrorCode)) {
            orderItem.unitsOrdered = orderItem.originalUnits;
            orderItem.eachesOrdered = orderItem.breakable
              ? orderItem.originalEaches
              : 0;
            orderItems.push(orderItem);
          }
        });
        order.orderItems = orderItems;
        ordersToSubmit.push(order);
      }
    });
    this.orderTracingService.trackSubmitImportOrderStart(ordersToSubmit);
    this.importOrderService.submit(ordersToSubmit);
  }

  updateQuantity(
    otIdx: number,
    oiIdx: number,
    isValidOrder: boolean,
    newQuantity: QuantityUpdate,
  ) {
    const oi = isValidOrder
      ? { ...this.validOrders[otIdx]?.orderItems[oiIdx] }
      : { ...this.invalidOrders[otIdx]?.orderItems[oiIdx] };
    let enableDelete = false;
    if (!!oi) {
      if (newQuantity.isEaches) {
        enableDelete = newQuantity.quantity < 1 && oi.unitsOrdered < 1;
        oi.eachesOrdered = enableDelete
          ? oi.eachesOrdered
          : newQuantity.quantity;
      } else {
        enableDelete =
          newQuantity.quantity < 1 &&
          (!oi.subOrReplacementProduct?.breakable || oi.eachesOrdered < 1);
        oi.unitsOrdered = enableDelete ? oi.unitsOrdered : newQuantity.quantity;
      }
      if (isValidOrder) {
        this.validOrders[otIdx].orderItems[oiIdx] = oi;
      } else {
        this.invalidOrders[otIdx].orderItems[oiIdx] = oi;
      }

      if (enableDelete) {
        this.confirmRemove(otIdx, oiIdx, isValidOrder);
      }
    }
  }

  getTotalUnits(otIdx: number, isValidOrder?: boolean): number {
    let order = isValidOrder
      ? this.validOrders[otIdx]
      : this.invalidOrders[otIdx];
    if (!order) return 0;
    return order.orderItems.reduce(
      (accumulator, oi) => accumulator + (oi?.originalUnits || 0),
      0,
    );
  }

  getTotalEaches(otIdx: number, isValidOrder?: boolean): number {
    let order = isValidOrder
      ? this.validOrders[otIdx]
      : this.invalidOrders[otIdx];
    if (!order) return 0;
    return order.orderItems.reduce(
      (accumulator, oi) =>
        accumulator + (oi?.breakable ? oi?.originalEaches || 0 : 0),
      0,
    );
  }

  allowDownloadOption(): boolean {
    let containsDownloadableInvalidOrderItem = false;
    const allOrders = this.invalidOrders.concat(this.validOrders);
    for (let order of allOrders) {
      if (INVALID_ORDER_ITEM.includes(order?.orderHeaderErrorCode)) {
        containsDownloadableInvalidOrderItem = true;
      } else {
        order?.orderItems.forEach(oi => {
          if (!!oi && INVALID_ORDER_ITEM.includes(oi.orderItemErrorCode)) {
            containsDownloadableInvalidOrderItem = true;
          }
        });
      }
    }
    return containsDownloadableInvalidOrderItem;
  }

  downloadImportOrderValidation = async () => {
    const allOrders = this.invalidOrders.concat(
      this.validOrders?.filter(oi => !!oi),
    );
    if (!this.confirmModalOpen) {
      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        DownloadImportOrderModalComponent,
        {
          orders: allOrders,
        },
        'modal-desktop-size-lg',
      );

      const modal = await this.modalController?.create(
        this.modalService.modalOptions,
      );

      modal?.onDidDismiss().then((resp: any) => {
        if (resp?.data?.dispatchAction) {
          this.downloadFileEmitterHandler({
            fileName: resp.data.fileInfo.fileName,
            importedOrders: allOrders,
            customers: this.customers,
          });
        }
        this.confirmModalOpen = false;
      });
      this.confirmModalOpen = true;
      return await modal.present();
    }
  };
  async downloadFileEmitterHandler({ fileName, importedOrders, customers }) {
    this.importOrderDownloadService.handleDownloadImportOrderValidation(
      fileName,
      importedOrders,
      customers,
    );
  }

  willSplit(otIdx: number, isValidOrder: boolean): boolean {
    const orderItems = isValidOrder
      ? this.validOrders[otIdx]?.orderItems
      : this.invalidOrders[otIdx]?.orderItems;
    let hasDifferentVendor = false;
    let hasSpecialVendor = false;
    if (!!orderItems) {
      let vendorId = -1;
      for (const oi of orderItems) {
        if (!oi) continue;
        if (vendorId === -1) vendorId = oi.purchasedFromVendor;
        if (oi.specialVendor) hasSpecialVendor = true;
        if (vendorId !== oi.purchasedFromVendor) hasDifferentVendor = true;
        if (hasSpecialVendor && hasDifferentVendor) return true;
      }
    }
    return false;
  }

  isValidOrderWithInvalidProduct(order: ImportOrderTransaction) {
    const orderItems = order?.orderItems?.filter(oi => !!oi) ?? [];
    return (
      !order?.orderHeaderErrorCode &&
      orderItems.filter(
        oi =>
          CRITICAL_ERRORS.includes(oi.orderItemErrorCode) ||
          MINOR_ERRORS.includes(oi.orderItemErrorCode),
      ).length < orderItems.length
    );
  }

  async presentCalendarPopover(
    event: any,
    otIdx: number,
    isValidOrder?: boolean,
  ) {
    event?.stopPropagation();

    let order = isValidOrder
      ? this.validOrders[otIdx]
      : this.invalidOrders[otIdx];
    if (!order) return;
    this.eligibleDeliveryDays =
      this.customersDeliveryDays[order.customerNumber];
    this.initialDeliveryDate =
      moment().add(1, 'd').date() ||
      this.customersConfirmDeliveryDates[order.customerNumber];

    this.modalService.setModalOptions(
      this.platformService.isTouch.value,
      CalendarComponent,
      {
        calendarTitle: this.translate.instant(
          'i18n.orderPage.changeDeliveryDate',
        ),
        currentSelectedDate: order.confirmedDeliveryDate,
        dateClass: this.dateClass,
        dateFilter: this.dateFilter,
      },
      'modal-calendar-desktop-size-lg',
    );
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );

    modal.onDidDismiss().then((resp: any) => {
      if (resp && resp.data && resp.data.selectedDate) {
        order.requestedDeliveryDate = resp.data.selectedDate;
        order.confirmedDeliveryDate = resp.data.selectedDate;
      }
    });
    return await modal.present();
  }

  showSubstituteFor(otIdx: number, oiIdx: number, isValidOrder: boolean): void {
    let currSubs = isValidOrder
      ? this.substitutesForValidOrders.get(otIdx) || []
      : this.substitutesForInvalidOrders.get(otIdx) || [];
    currSubs.push(oiIdx);
    //LOGIC TO SET 'CS' VALUE FROM BREAKABLE PRODUCT TO NON BREAKABLE SUBSTITUTE
    if (isValidOrder) {
      const oi = this.validOrders[otIdx].orderItems[oiIdx];
      const cs =
        oi.breakable &&
        !oi.unitsOrdered &&
        oi.subOrReplacementProduct?.breakable === false
          ? oi.eachesOrdered
          : oi.unitsOrdered;
      oi.unitsOrdered = cs;
      this.substitutesForValidOrders.set(otIdx, currSubs);
    } else {
      const oi = this.invalidOrders[otIdx].orderItems[oiIdx];
      const cs =
        oi.breakable &&
        !oi.unitsOrdered &&
        oi.subOrReplacementProduct?.breakable === false
          ? oi.eachesOrdered
          : oi.unitsOrdered;
      oi.unitsOrdered = cs;
      this.substitutesForInvalidOrders.set(otIdx, currSubs);
    }
  }

  replaceOriginalWithSubstitute(
    otIdx: number,
    oiIdx: number,
    isValidOrder: boolean,
  ) {
    const otCopy = isValidOrder
      ? { ...this.validOrders[otIdx] }
      : { ...this.invalidOrders[otIdx] };
    const oiCopy = { ...otCopy.orderItems[oiIdx] };
    const sbsCopy = { ...oiCopy.subOrReplacementProduct };
    //FOR ORDER ITEM -> REMOVE ORDER ERROR & REPLACE PROPERTIES
    oiCopy.orderItemErrorCode = '';
    oiCopy.orderItemErrorMsg = '';
    oiCopy.subOrReplacementProduct = null;
    oiCopy.substituteFor = oiCopy.productNumber;
    //COPY SUBSTITUTE INFORMATION
    Object.keys(sbsCopy).forEach(key => (oiCopy[key] = sbsCopy[key]));
    oiCopy.originalUnits = oiCopy.unitsOrdered;
    oiCopy.originalEaches = oiCopy.eachesOrdered;
    //LOOK FOR DUPLICATE SUB AND KEEP HIGHEST QTY
    let isDup = false;
    const duplicatesIdx = [];
    otCopy?.orderItems.forEach((oi, idx) => {
      if (!!oi && oi.productNumber === sbsCopy.productNumber) {
        //DUPLICATE FOUND
        if (
          oi.originalUnits > oiCopy.originalUnits ||
          (oi.originalUnits === oiCopy.originalUnits &&
            oi.originalEaches > oiCopy.originalEaches)
        ) {
          isDup = true;
        } else {
          //REMOVE DUPLICATE ITEM
          duplicatesIdx.push(idx);
        }
      }
    });
    if (isDup) {
      //DUPLICATE SUBSTITUTE -> REMOVE
      this.removeOrderItem(otIdx, oiIdx, isValidOrder);
    } else {
      otCopy.orderItems[oiIdx] = oiCopy;
      //REMOVE FROM SUBSTITUTES
      const sbs = isValidOrder
        ? this.substitutesForValidOrders.get(otIdx)
        : this.substitutesForInvalidOrders.get(otIdx);
      const subIdx = sbs?.indexOf(oiIdx);
      sbs?.splice(subIdx, 1);
      //REMOVE DUPLICATES ITEMS
      duplicatesIdx.forEach(idx => otCopy.orderItems.splice(idx, 1));
      if (isValidOrder) {
        this.validOrders[otIdx] = otCopy;
        this.substitutesForValidOrders.set(otIdx, sbs);
      } else {
        //INVALID ORDER -> CHECK TO MOVE ORDER TO VALID
        const counter = this.orderItemsCounter(otCopy);
        if (counter.invalid === 0) {
          //NO INVALID ITEMS -> SET ORDER AS VALID
          this.removeOrder(otIdx);
          const idx = this.validOrders.push(otCopy);
          this.substitutesForInvalidOrders.delete(otIdx); //DELETE SUBSTITUTE (substitutesForInvalidOrders)
          otIdx = idx - 1;
          isValidOrder = true;
          this.substitutesForValidOrders.set(otIdx, sbs);
        } else {
          this.invalidOrders[otIdx] = otCopy;
          this.substitutesForInvalidOrders.set(otIdx, sbs);
        }
      }
    }
  }

  isAbleToOrdering(order: ImportOrderTransaction) {
    return (
      order.orderHeaderErrorCode !==
      ImportOrderValidationErrorTypeEnum.CRITICAL_ERROR_102
    );
  }

  get validOrdersCounter(): number {
    return this.validOrders?.filter(o => !!o)?.length;
  }

  get invalidOrdersCounter(): number {
    return this.invalidOrders?.filter(o => !!o)?.length;
  }

  get validProductsCounter(): number {
    return this.validOrders
      .concat(this.invalidOrders)
      .filter(o => !!o)
      .reduce(
        (accumulator, order) =>
          accumulator + (this.orderItemsCounter(order).valid || 0),
        0,
      );
  }

  get invalidProductsCounter(): number {
    return this.validOrders
      .concat(this.invalidOrders)
      ?.filter(o => !!o)
      .reduce(
        (accumulator, order) =>
          accumulator + (this.orderItemsCounter(order).invalid || 0),
        0,
      );
  }

  get isSubmitAllowed(): boolean {
    const checkMinCsEa = (order: ImportOrderTransaction) => {
      if (!order) return 0;
      for (const oi of order.orderItems) {
        if (!oi) continue;
        if (oi.originalEaches > 0 || oi.originalUnits > 0) {
          return 1;
        }
      }
      return 0;
    };
    if (this.validOrdersCounter > 0) {
      return (
        this.validOrders.reduce(
          (accumulator, order) => accumulator + checkMinCsEa(order),
          0,
        ) > 0
      );
    } else if (this.invalidOrdersCounter > 0) {
      for (let order of this.invalidOrders) {
        if (!!order && this.isValidOrderWithInvalidProduct(order)) {
          return checkMinCsEa(order) > 0;
        }
      }
    }
    return false;
  }

  goToTop() {
    this.content.scrollToTop();
  }
}
